import { useState, useEffect } from "react";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import Autocomplete from '@mui/material/Autocomplete';

export default function LocationDetails({
    newLocation,
    setNewLocation,
    errors
}) {
    const [disableFields, setDisabledFields] = useState([]); // Fields enabled based on location type
    
    const cities = ["Smith Falls", "Kanata"];
    const locationTypes = ["Lane/Rack/Shelf", "Lane/Rack/Shelf/Drawer", "Room", "Room/Rack/Shelf", "Room/Rack/Shelf/Drawer"];
    const commonProps = { // Props common for the input fields
        display: "flex",
        flexDirection: "column",
        fullWidth: true,
        marginBottom: ".5rem",
        marginTop: ".5rem",
    };

    // On component mount, set the initial state of disabled fields based on existing locationType
    useEffect(()=>{
       handleLocationChange({target: {name: "locationType", value: newLocation["locationType"] || ""}}) 
    },[])

    const handleLocationChange = (e)=>{
        
        setNewLocation({
            ...newLocation,
            [e.target.name]: e.target.value
        })
        
        if (e.target.name === "locationType" && e.target.value) {
            // normalize to lowercase and trim before splitting
            const enabledFields = e.target.value.toLowerCase().split("/").map(s => s.trim());
            const locationTypeFields = ["room", "lane", "rack", "shelf", "drawer"];
            const toDisable = locationTypeFields.filter(field => !enabledFields.includes(field));
            setDisabledFields(toDisable);
        }
    }

    const ErrorText = ({name}) => (
        errors[name] && (
            <MDTypography variant="caption" color="error" ml={0.5}>
                {errors[name]}
            </MDTypography>
        )
    )

    return (
        <>
            <MDBox>
                <MDTypography variant="h5" fontWeight="bold">
                    Location Details
                </MDTypography>
            </MDBox>

            {/* City */}
            <MDBox mt='1rem' >
                <FormControl fullWidth>
                    <Autocomplete
                        id="outlined-adornment-city"
                        options={cities}
                        error={errors.city ? true : false}
                        onChange={(e, value) => 
                            handleLocationChange({target: {name: "city", value: value}})
                        }
                        value={newLocation["city"] || ""}
                        renderInput={(params) => <MDInput {...params} label="City" />}
                        sx={{ '& .MuiInputBase-root': { height: '45px' } }}
                    />
                </FormControl>
                <ErrorText name="city" />
            </MDBox>

            {/* Location Type Dropdown */}
            <MDBox {...commonProps} mb={3}>
                <MDBox mb={0.5}>
                    <MDTypography variant="body2" color="text">
                        Choose the location type that best describes this location.
                    </MDTypography>
                </MDBox>

                <FormControl fullWidth>
                    <Autocomplete
                        id="outlined-adornment-location-type"
                        label="Location Type"
                        options={locationTypes}
                        renderInput={(params) => <MDInput {...params} label="Location Type" />}
                        name="locationType"
                        onChange={(e, value) => 
                            handleLocationChange({target: {name: "locationType", value: value}})
                        }
                        value={newLocation["locationType"] || ""}
                        sx={{ '& .MuiInputBase-root': { height: '45px' } }}

                    />
                </FormControl>
                <ErrorText name="locationType" />
            </MDBox>

            {/* Room */}
            <MDBox {...commonProps}>
                <MDInput
                    label="Room"
                    fullWidth
                    disabled={disableFields.includes("room")}
                    name="room"
                    error={errors.Room ? true : false}
                    onChange={handleLocationChange}
                    value={newLocation["room"] || ""}
                />
                <ErrorText name="room" />
            </MDBox>

            <Grid container spacing={{ xs: 0, lg: 2 }} >

                {/* Lane */}
                <Grid item xs={12} lg={4}>
                    <MDBox {...commonProps}>
                        <FormControl fullWidth>
                            <MDInput
                                id="outlined-adornment-location"
                                label="Lane"
                                disabled={disableFields.includes("lane")}
                                name="lane"
                                onChange={handleLocationChange}
                                error={errors.Lane ? true : false}
                                value={newLocation["lane"] || ""}
                                placeholder="E.g., L1"
                                fullWidth
                            />
                        </FormControl>
                        <ErrorText name="lane" />
                    </MDBox>
                </Grid>

                {/* Rack */}
                <Grid item xs={12} lg={4}>
                    <MDBox {...commonProps}>
                        <MDInput
                            label="Rack"
                            hintText="Rx"
                            disabled={disableFields.includes("rack")}
                            name="rack"
                            onChange={handleLocationChange}
                            error={errors.Rack ? true : false}
                            value={newLocation["rack"] || ""}
                            placeholder="E.g., R1"
                            fullWidth
                        />
                        <ErrorText name="rack" />
                    </MDBox>
                </Grid>

                {/* Shelf */}
                <Grid item xs={12} lg={4}>
                    <MDBox {...commonProps}>
                        <MDInput
                            label="Shelf (Range)"
                            hintText="Sx"
                            disabled={disableFields.includes("shelf")}
                            name="shelf"
                            onChange={handleLocationChange}
                            error={errors.Shelf ? true : false}
                            value={newLocation["shelf"] || ""}
                            placeholder="E.g., S1-S4"
                            fullWidth
                        />
                        <ErrorText name="shelf" />
                    </MDBox>
                </Grid>
            </Grid>

            {/* Drawer */}
            <MDBox {...commonProps}>
                <MDInput
                    label="Drawer (Range)"
                    disabled={disableFields.includes("drawer")}
                    name="drawer"
                    onChange={handleLocationChange}
                    error={errors.Drawer ? true : false}
                    value={newLocation["drawer"] || ""}
                    placeholder="E.g., D100-D109"
                    fullWidth
                />
                <ErrorText name="drawer" />
            </MDBox>
        </>
    )
}